import { DialogReceipt, TableRecipes } from "@components";
import DialogObservation from "@components/Dialog/Observation";
import TransactionEntryDialog from "@components/Dialog/TransactionEntry";
import { useTransactionImport } from "@components/Dialog/TransactionEntry/useTransactionImport";
import { isImportSettled } from "@components/Dialog/TransactionEntry/importState";
import { downloadImportTemplate } from "@components/Dialog/TransactionEntry/importTemplate";
import DialogAudit from "@components/Dialog/Audit";
import RecurrenceScopeDialog from "@components/Dialog/RecurrenceScope";
import DialogRecurrenceDetails from "@components/Dialog/RecurrenceDetails";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import { getTransactionSaveErrorLabelKey } from "@utils/apiError";
import { resolveTransactionStatus } from "@utils/transactionStatus";
import { resolvePaymentMethodLabel } from "@utils/paymentMethodLabel";
import {
  buildRecurrencePayload,
  parseRecurrenceFromApi,
  toBackendStatus,
  toFormDate,
} from "@utils/transactionRecurrence";
import SelectedFilters from "@components/SelectedFilters";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import { MonthNavigation } from "@components/Dashboard";
import { ReceiptStatus, Vehicle } from "@components/Table/Recipes/Table";
import { Notification } from "@rio-cloud/rio-uikit";
import {
  createTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  exportTransactions,
  getDetailTransaction,
  getDRESummary,
  getFile,
  getTableData,
} from "@services";
import { useAppDispatch } from "@store";
import { setEndDate, setStartDate } from "@store/date/dateSlice";
import { ITransactionPayload, RioTrip } from "@types";
import { useT, useRecurrenceDelete, useRecurrenceEdit, useDebounce } from "@hooks";
import { useIntl } from "react-intl";
import { formatValue, parseCurrencyBRL } from "@utils";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormState } from "src/components/Dialog/Receipt/form";
import TransactionKpiCard, { CURRENCY_KPI_ICON, calculateDelta } from "@screens/shared/TransactionKpiCard";
import ConfirmDeleteDialog from "@screens/shared/ConfirmDeleteDialog";
import {
  isTransactionDeletable,
  MAX_DRIVERS,
  resolveMomentLocale,
  resolveStatusAmounts,
  resolveTripDriverId,
  TransactionTableItem,
  type TransactionStatusPeriod,
} from "@screens/shared/transactionScreenUtils";
import { resolveEditTrip, resolveTripLabel } from "@utils/rioTripOptions";
import { useTransactionFilters } from "@screens/shared/useTransactionFilters";
import { useTransactionListData } from "@screens/shared/useTransactionListData";
import { useTransactionOriginFilters } from "@screens/shared/useTransactionOriginFilters";
import { useTransactionBatchDelete } from "@screens/shared/useTransactionBatchDelete";
import { useBatchDeletePermission } from "@screens/shared/useBatchDeletePermission";
import { getBatchDeletePermission } from "@services/form";
import BatchDeleteDialog from "@components/Dialog/BatchDelete";
import { useMonthPeriodNavigation } from "@screens/shared/useMonthPeriodNavigation";

const SEARCH_DEBOUNCE = 2000;

const formatKpiComparisonCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const RecipesScreen = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE);

  const { startDate, endDate, isTreeReady, requestFilters, listFilters, previousMonthFilters } =
    useTransactionFilters();

  const kpiPeriodRange = formatPeriodRange(startDate, endDate);

  useEffect(() => {
    if (searchParams.get("add") === "receipt") {
      setShowEntryDialog(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [selected, setSelected] = useState<FormState & { id: string }>();
  const [selectedTrip, setSelectedTrip] = useState<RioTrip | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [recurrenceDetailsId, setRecurrenceDetailsId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [auditRecipeId, setAuditRecipeId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);

  const handleSave = async (data: FormState, callback?: () => void, dialogModal = true) => {
    if (selected?.id && recurrenceEdit.requestEdit(selected.id, data, callback)) return;

    await submitRecipe(data, callback, dialogModal);
  };

  const submitRecipe = async (data: FormState, callback?: () => void, dialogModal = true) => {
    const payload: ITransactionPayload = {
      amount: parseCurrencyBRL(data?.value),
      branchId: Number(data?.branch),
      categoryId: Number(data?.category),
      description: data?.observation,
      driverIds: data?.driver.length > 0 ? data?.driver.slice(0, MAX_DRIVERS) : null,
      vehicleId: data?.vehicle ? data?.vehicle : null,
      segmentBussinessId: Number(data?.segment),
      subcategoryId: Number(data?.subcategory),
      supplierClientId: Number(data?.supplier),
      taxPercentage: Number(data?.taxes),
      transactionDate: data?.date,
      tripCode: data.trip || null,
      transactionType: "RECEITA",
      status: toBackendStatus(data.status),
      dueDate: data.dueDate || null,
      recurrence: buildRecurrencePayload(data),
      attachmentUrl: file ? undefined : attachmentUrl || null,
      fileNameAttachmentUrl: file ? undefined : fileName || null,
    };

    const messages = {
      success: selected ? t("recipe.notification.editSuccess") : t("recipe.notification.createSuccess"),
      errorKey: selected ? "recipe.notification.editError" : "recipe.notification.createError",
    };

    try {
      selected
        ? await editTransaction(selected.id, payload, file || undefined)
        : await createTransaction(payload, file || undefined);

      if (dialogModal) {
        setOpenModal(false);
      } else {
        setFile(null);
        setFileName("");
        setAttachmentUrl(null);
      }
      if (!selected) {
        setFile(null);
        setFileName("");
        setAttachmentUrl(null);
      }
      Notification.success(messages.success);
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(t(getTransactionSaveErrorLabelKey(error, messages.errorKey)));
    }
  };

  const toReceiptStatus = (item: TransactionTableItem): ReceiptStatus => {
    const status = resolveTransactionStatus(item.status, item.dueDate, item.transactionDate);
    return status === "vencido" ? "emAtraso" : status;
  };

  const toVehicleRow = (item: TransactionTableItem): Vehicle => ({
    id: item.id,
    date: moment(item.dueDate || item.transactionDate).format("DD/MM/YYYY"),
    amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
    branch: item.branchName,
    segment: item.segmentBussinessName,
    customer: item.clientName,
    vehicle: item.vehiclePlate,
    driver: item.driverName,
    driverIds: item.driverIds || [],
    category: item.categoryName,
    subcategory: item.subcategoryName,
    taxes: `${item.taxPercentage}%`,
    status: toReceiptStatus(item),
    recurrenceId: item.recurrenceId ?? null,
    source: item.source ?? null,
    trip: resolveTripLabel(item.trip, item.tripCode),
    tripId: item.trip?.id ?? item.tripCode ?? null,
    tripDriverId: resolveTripDriverId(item),
    paymentMethod: resolvePaymentMethodLabel(item.paymentMethod, t),
    deletable: isTransactionDeletable(item),
    version: item.version ?? null,
    amountValue: item.amount,
    installmentNumber: item.installmentNumber ?? null,
    totalInstallments: item.totalInstallments ?? null,
  });

  const calculateStatusTotals = (rows: Vehicle[]): Record<ReceiptStatus, number> => {
    const totals: Record<ReceiptStatus, number> = { previsto: 0, realizado: 0, emAtraso: 0 };
    rows.forEach((row) => {
      totals[row.status as ReceiptStatus] += parseCurrencyBRL(row.amount);
    });
    return totals;
  };

  const selectSummaryStatusTotals = (
    data: any,
    period: TransactionStatusPeriod
  ): Record<ReceiptStatus, number> | null => {
    const amounts = resolveStatusAmounts(data?.revenuesKpis, period);
    if (!amounts) return null;

    return { previsto: amounts.forecasted, realizado: amounts.realized, emAtraso: amounts.overdue };
  };

  const originFilterState = useTransactionOriginFilters("RECEITA");

  const {
    tableData,
    totalRecords,
    isLoadingTable,
    isLoadingKpis,
    isLoadingMore,
    totalAmount,
    previousTotalAmount,
    previousKpiTotalsByStatus,
    kpiTotalsByStatus,
    kpiComparisons,
    fetchInitial,
    handleLoadMore,
  } = useTransactionListData<Vehicle, Record<ReceiptStatus, number>>({
    transactionType: "RECEITA",
    services: { getTableData, getDRESummary },
    startDate,
    endDate,
    isTreeReady,
    listFilters,
    previousMonthFilters,
    search: debouncedSearchValue,
    originFilters: originFilterState.originFilters,
    loadErrorMessage: t("recipe.notification.loadError"),
    mapRow: toVehicleRow,
    calculateStatusTotals,
    selectSummaryTotal: (data) => data.totalReceitas,
    selectSummaryComparisons: (data) => data?.revenuesKpis,
    selectSummaryStatusTotals,
  });

  const canBatchDelete = useBatchDeletePermission(getBatchDeletePermission);

  const batchDelete = useTransactionBatchDelete({
    type: "RECEITA",
    rows: tableData,
    totalRecords,
    startDate,
    endDate,
    search: debouncedSearchValue,
    listFilters,
    originFilters: originFilterState.originFilters,
    onDeleted: fetchInitial,
    canBatchDelete,
  });

  const recurrenceEdit = useRecurrenceEdit<FormState>({
    rows: tableData,
    successLabelKey: "recurrence.notification.editSuccess",
    errorLabelKey: "recipe.notification.editError",
    onSaved: () => {
      setOpenModal(false);
      setSelected(undefined);
      fetchInitial();
    },
    getAmount: (data) => parseCurrencyBRL(data.value),
    buildPayload: (data, scope, targetTransactionId) => ({
      scope,
      targetTransactionId,
      branchId: Number(data.branch),
      segmentBussinessId: Number(data.segment),
      supplierClientId: Number(data.supplier),
      categoryId: Number(data.category),
      subcategoryId: Number(data.subcategory),
      vehicleId: data.vehicle || null,
      driverIds: data.driver.length > 0 ? data.driver.slice(0, MAX_DRIVERS) : null,
      tripCode: data.trip || null,
      description: data.observation,
      taxPercentage: Number(data.taxes),
      status: toBackendStatus(data.status),
      amount: scope === "ALL_GROUP" ? undefined : parseCurrencyBRL(data.value),
    }),
    submitSingle: (data, callback) => submitRecipe(data, callback),
  });

  const recurrenceDelete = useRecurrenceDelete({
    rows: tableData,
    successLabelKey: "recipe.notification.deleteSuccess",
    errorLabelKey: "recipe.notification.deleteError",
    onDeleted: fetchInitial,
  });

  const handleDeleteRecipe = async (id: string) => {
    if (recurrenceDelete.requestDelete(id)) return;

    setDeleteConfirmId(id);
  };

  const handleDeleteFileRecipe = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileRecipe = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success(t("common.notification.fileDeleteSuccess"));
      fetchInitial();
    } catch {
      Notification.error(t("common.notification.fileDeleteError"));
    }
  };

  const confirmDeleteRecipe = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success(t("recipe.notification.deleteSuccess"));
      fetchInitial();
    } catch {
      Notification.error(t("recipe.notification.deleteError"));
    }
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);

      window.open(response.data.downloadUrl, "_blank");
      Notification.success(t("common.notification.downloadSuccess"));
    } catch {
      Notification.error(t("common.notification.downloadError"));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      setFile(null);
      const response = await getDetailTransaction(id);

      if (response?.data?.fileNameAttachmentUrl) {
        setFileName(response?.data?.fileNameAttachmentUrl);
      } else if (response?.data?.attachmentUrl) {
        setFileName(response.data.attachmentUrl);
      } else {
        setFileName("");
      }

      if (response?.data?.attachmentUrl) {
        setAttachmentUrl(response.data.attachmentUrl);
      } else {
        setAttachmentUrl(null);
      }

      const driversFromApi = response?.data?.driverIds || (response?.data?.driverId ? [response?.data?.driverId] : []);
      const limitedDrivers = Array.isArray(driversFromApi) ? driversFromApi.slice(0, MAX_DRIVERS) : [];

      const detail = response?.data;
      const canonicalStatus = resolveTransactionStatus(detail?.status, detail?.dueDate, detail?.transactionDate);

      const mount: FormState & { id: string } = {
        status: canonicalStatus === "vencido" ? "emAtraso" : canonicalStatus,
        dueDate: toFormDate(detail?.dueDate),
        source: detail?.source ?? "",
        ...parseRecurrenceFromApi(detail?.recurrence, detail?.recurrenceId),
        branch: response?.data?.branchId?.toString(),
        category: response?.data?.categoryId?.toString(),
        date: response?.data?.transactionDate,
        segment: response?.data?.segmentBussinessId?.toString(),
        driver: limitedDrivers,
        subcategory: response?.data?.subcategoryId?.toString(),
        observation: response?.data?.description,
        supplier: response?.data?.supplierClientId?.toString(),
        taxes: response?.data?.taxPercentage,
        trip: response?.data?.trip?.id ?? response?.data?.tripCode,
        value: formatValue(response?.data?.amount, false),
        vehicle: response?.data?.vehicleId?.toString(),
        id: response.data.id,
      };
      setSelected(mount);
      setSelectedTrip(resolveEditTrip(response?.data?.trip, response?.data?.tripCode));
      setOpenModal(true);
    } catch {
      Notification.error(t("recipe.notification.detailError"));
    }
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleDownload = async () => {
    try {
      await exportTransactions(startDate, endDate, "RECEITA");
      Notification.success(t("recipe.notification.exportSuccess"));
    } catch {
      Notification.error(t("recipe.notification.exportError"));
    }
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch {
      Notification.error(t("recipe.notification.detailError"));
      handleObservationDefault();
    }
  };

  const handleOnCLose = () => {
    setOpenModal(false);
    setSelected(undefined);
    setSelectedTrip(null);
    setFile(null);
    setFileName("");
    setAttachmentUrl(null);
    setIsCreate(false);
  };

  const { currentMonth, onPressLeftPeriod, onPressRightPeriod } = useMonthPeriodNavigation(startDate);

  const [isCreate, setIsCreate] = useState(false);

  const receiptImport = useTransactionImport("RECEITA", {
    isDialogOpen: showEntryDialog,
    onImported: () => {
      fetchInitial();
      originFilterState.reloadImports();
    },
  });

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm height-100pct">
      <MonthNavigation currentMonth={currentMonth} onPrevMonth={onPressLeftPeriod} onNextMonth={onPressRightPeriod} />
      <SelectedFilters requestFilters={requestFilters} />
      {isLoadingTable || isLoadingKpis ? (
        <KpiCardsSkeleton contentGapClassName="gap-10 gap-20-sm" />
      ) : (
        <div className="display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-10">
          <TransactionKpiCard
            label={t("receipt.kpi.realized")}
            comparison={kpiComparisons.realized}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("receipt.kpi.realized")}
                description={t("receipt.kpi.realized.tooltip")}
                lines={[kpiTotalsByStatus.realizado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.realizado, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.realizado, previousKpiTotalsByStatus?.realizado)}
          />
          <TransactionKpiCard
            label={t("receipt.kpi.forecast")}
            comparison={kpiComparisons.forecasted}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("receipt.kpi.forecast")}
                description={t("receipt.kpi.forecast.tooltip")}
                lines={[kpiTotalsByStatus.previsto.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.previsto, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.previsto, previousKpiTotalsByStatus?.previsto)}
          />
          <TransactionKpiCard
            label={t("receipt.kpi.overdue")}
            comparison={kpiComparisons.overdue}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("receipt.kpi.overdue")}
                description={t("receipt.kpi.overdue.tooltip")}
                lines={[kpiTotalsByStatus.emAtraso.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.emAtraso, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.emAtraso, previousKpiTotalsByStatus?.emAtraso)}
          />
          <TransactionKpiCard
            label={t("receipt.kpi.total")}
            comparison={kpiComparisons.total}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("receipt.kpi.total")}
                description={t("receipt.kpi.total.tooltip")}
                lines={[totalAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
                placement="bottom-end"
              />
            }
            value={formatValue(totalAmount, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(totalAmount, previousTotalAmount ?? undefined)}
          />
        </div>
      )}
      <div className="flex-1-1 height-100pct">
        <TableRecipes
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          data={tableData}
          totalData={totalRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={handleDownload}
          handleCreate={() => setShowEntryDialog(true)}
          handleDelete={handleDeleteRecipe}
          onDeleteFile={handleDeleteFileRecipe}
          onGetFile={getDownloadFile}
          handleEdit={handleEdit}
          handleObservation={handleObservation}
          handleAudit={(id) => setAuditRecipeId(id)}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
          selection={batchDelete.tableSelection}
          selectionBar={batchDelete.selectionBar}
          originFilters={{
            origins: originFilterState.origins,
            importIds: originFilterState.importIds,
            imports: originFilterState.imports,
            isLoadingImports: originFilterState.isLoadingImports,
            onOriginsChange: originFilterState.setOrigins,
            onImportIdsChange: originFilterState.setImportIds,
          }}
        />
        <BatchDeleteDialog {...batchDelete.dialog} />
      </div>
      <DialogRecurrenceDetails
        show={recurrenceDetailsId !== null}
        module="RECEITA"
        transactionId={recurrenceDetailsId}
        recurrenceId={tableData.find((item) => item.id === recurrenceDetailsId)?.recurrenceId}
        onClose={() => setRecurrenceDetailsId(null)}
      />

      <TransactionEntryDialog
        show={showEntryDialog}
        title={t("recipe.dialog.newTitle")}
        manualTitle={t("receipt.entry.manual.title")}
        spreadsheetTitle={t("receipt.entry.spreadsheet.title")}
        importType="RECEITA"
        importState={receiptImport.state}
        onDownloadTemplate={() => downloadImportTemplate("RECEITA")}
        onSpreadsheetContinue={receiptImport.start}
        onImportConclude={() => {
          receiptImport.reset();
          setShowEntryDialog(false);
        }}
        onClose={() => {
          if (isImportSettled(receiptImport.state)) receiptImport.reset();
          setShowEntryDialog(false);
        }}
        onManualEntry={() => {
          setShowEntryDialog(false);
          setSelected(undefined);
          setSelectedTrip(null);
          setFile(null);
          setFileName("");
          setAttachmentUrl(null);
          setOpenModal(true);
          setIsCreate(true);
        }}
      />

      <DialogReceipt
        onViewRecurrence={() => setRecurrenceDetailsId(selected?.id ?? null)}
        title={selected ? t("recipe.dialog.editTitle") : t("recipe.dialog.newTitle")}
        show={openModal}
        onClose={handleOnCLose}
        onSave={handleSave}
        selected={selected}
        selectedTrip={selectedTrip}
        handleFile={(file) => setFile(file)}
        handleClearFile={() => {
          setFile(null);
          setAttachmentUrl(null);
        }}
        fileName={fileName}
        changeFileName={(filename) => setFileName(filename)}
        isCreate={isCreate}
        onDownloadFile={getDownloadFile}
        hasNewAttachment={!!file}
      />
      <DialogObservation
        onClose={handleObservationDefault}
        show={observation.open}
        title={t("common.label.observations")}
        value={observation.value}
      />

      <DialogAudit
        show={!!auditRecipeId}
        onClose={() => setAuditRecipeId(null)}
        transactionId={auditRecipeId}
        entityLabel={t("recipe.audit.label.recipe")}
      />

      <RecurrenceScopeDialog
        show={recurrenceEdit.isOpen}
        mode="edit"
        isSubmitting={recurrenceEdit.isSaving}
        onClose={recurrenceEdit.cancelEdit}
        onConfirm={recurrenceEdit.confirmEdit}
      />

      <RecurrenceScopeDialog
        show={recurrenceDelete.target !== null}
        mode="delete"
        isSubmitting={recurrenceDelete.isDeleting}
        onClose={recurrenceDelete.cancelDelete}
        onConfirm={recurrenceDelete.confirmDelete}
      />

      <ConfirmDeleteDialog
        show={deleteConfirmId !== null}
        title={t("recipe.dialog.deleteTitle")}
        message={t("recipe.dialog.deleteConfirm")}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={confirmDeleteRecipe}
      />

      <ConfirmDeleteDialog
        show={deleteConfirmFileId !== null}
        title={t("common.dialog.deleteReceiptTitle")}
        message={t("common.dialog.deleteReceiptConfirm")}
        onClose={() => setDeleteConfirmFileId(null)}
        onConfirm={confirmDeleteFileRecipe}
      />
    </div>
  );
};

export default RecipesScreen;
